import { useState, useRef, useEffect } from "react";
import { RECORDER_CONFIG } from "./recorder/store";

export function useAudioLevel(args: {
  stream: MediaStream;
  smoothing?: number;
  channels: number;
  fftSize?: number;
  sensitivity?: number;
  updateRate?: number;
}) {
  const {
    smoothing = 0.8,
    channels = 3,
    fftSize = 256 * 2,
    sensitivity = 0.9,
    updateRate = 30,
    stream,
  } = args;
  const [levels, setLevels] = useState(Array(channels).fill(0));
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const smoothRef = useRef(Array(channels).fill(0));
  const animationRef = useRef<number>(0);
  const lastUpdateRef = useRef<number>(0);
  const channelsRef = useRef(channels);
  const smoothingRef = useRef(smoothing);
  const fftSizeRef = useRef(fftSize);
  const sensitivityRef = useRef(sensitivity);
  const updateRateRef = useRef(updateRate);
  const prevLevelsRef = useRef(Array(channels).fill(0));

  // Update refs when props change to avoid recreating the effect
  useEffect(() => {
    channelsRef.current = channels;
    smoothingRef.current = smoothing;
    fftSizeRef.current = fftSize;
    sensitivityRef.current = sensitivity;
    updateRateRef.current = updateRate;
  }, [channels, smoothing, fftSize, sensitivity, updateRate]);

  useEffect(() => {
    if (!stream) {
      // Clean up when stream is lost
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
      analyserRef.current = null;
      return;
    }

    // Check if stream has audio tracks before setting up audio processing
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      // No audio tracks available, clean up and return
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
      analyserRef.current = null;
      setLevels(Array(channelsRef.current).fill(0));
      return;
    }

    const AudioContextConstructor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new AudioContextConstructor({
      sampleRate: RECORDER_CONFIG.audioContext.sampleRate,
    });
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();

    analyser.fftSize = fftSizeRef.current;
    analyser.smoothingTimeConstant = smoothingRef.current;
    src.connect(analyser);

    audioContextRef.current = ctx;
    analyserRef.current = analyser;

    const freqData = new Uint8Array(analyser.frequencyBinCount);

    function loop(currentTime: number) {
      if (currentTime - lastUpdateRef.current > updateRateRef.current) {
        lastUpdateRef.current = currentTime;

        if (analyserRef.current) {
          analyser.getByteFrequencyData(freqData);

          // Focus on relevant frequency range (skip very low frequencies)
          const startFreq = Math.floor(freqData.length * 0.05);
          const endFreq = Math.floor(freqData.length * 0.4);
          const relevantData = freqData.slice(startFreq, endFreq);

          const bands = [];
          const halfChannels = Math.floor(channelsRef.current / 2);
          const step = Math.floor(relevantData.length / halfChannels);

          // Left side (mirrored)
          for (let i = halfChannels - 1; i >= 0; i--) {
            let sum = 0;
            for (let j = 0; j < step; j++) {
              sum += relevantData[i * step + j] || 0;
            }
            const val = Math.min(
              1,
              (sum / step / 255) * sensitivityRef.current,
            );
            const smoothIndex = halfChannels - 1 - i;
            smoothRef.current[smoothIndex] =
              smoothRef.current[smoothIndex] * smoothingRef.current +
              val * (1 - smoothingRef.current);
            bands.push(Math.max(0.05, smoothRef.current[smoothIndex]));
          }

          // Center bar if odd number of channels
          if (channelsRef.current % 2 === 1) {
            let sum = 0;
            const centerIndex = halfChannels;
            const centerStart = Math.floor(relevantData.length * 0.4);
            const centerEnd = Math.floor(relevantData.length * 0.6);
            for (let j = centerStart; j < centerEnd; j++) {
              sum += relevantData[j] || 0;
            }
            const val = Math.min(
              1,
              (sum / (centerEnd - centerStart) / 255) * sensitivityRef.current,
            );
            smoothRef.current[centerIndex] =
              smoothRef.current[centerIndex] * smoothingRef.current +
              val * (1 - smoothingRef.current);
            bands.push(Math.max(0.05, smoothRef.current[centerIndex]));
          }

          // Right side (normal order)
          for (let i = 0; i < halfChannels; i++) {
            let sum = 0;
            for (let j = 0; j < step; j++) {
              sum += relevantData[i * step + j] || 0;
            }
            const val = Math.min(
              1,
              (sum / step / 255) * sensitivityRef.current,
            );
            const smoothIndex = channelsRef.current - halfChannels + i;
            smoothRef.current[smoothIndex] =
              smoothRef.current[smoothIndex] * smoothingRef.current +
              val * (1 - smoothingRef.current);
            bands.push(Math.max(0.05, smoothRef.current[smoothIndex]));
          }

          // Only update state if levels have changed
          let hasChanged = false;
          for (let i = 0; i < bands.length; i++) {
            if (Math.abs(bands[i] - prevLevelsRef.current[i]) > 0.001) {
              hasChanged = true;
              break;
            }
          }

          if (hasChanged) {
            prevLevelsRef.current = bands;
            setLevels(bands);
          }
        }
      }

      animationRef.current = requestAnimationFrame(loop);
    }

    animationRef.current = requestAnimationFrame(loop);

    return () => {
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [stream]);

  return { stream, levels };
}
